/**
 * IPAMBlocks — /networks/blocks
 * Full block management: list, create (wizard), expand subnets, link to Explorer.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GlobeAltIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  QueueListIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import IPAMLayout from './IPAMLayout';
import IpamCascadeDeleteModal from './IpamCascadeDeleteModal';
import ListPage from './common/ListPage';
import api from '../services/api';
import { compareText } from '../utils/ipamSort';
import { useListView } from '../views/ViewsContext';

/* ── Standard block catalogue ─────────────────────────────────────────────── */
const IPV4_BLOCKS = [
  { cidr: '10.0.0.0/8',      label: 'Clase A corporativa',  desc: 'RFC 1918 — 16 M hosts', type: 'internal' },
  { cidr: '172.16.0.0/12',   label: 'Clase B corporativa',  desc: 'RFC 1918 — 1 M hosts',  type: 'internal' },
  { cidr: '192.168.0.0/16',  label: 'Clase C corporativa',  desc: 'RFC 1918 — 65 k hosts',  type: 'internal' },
  { cidr: '100.64.0.0/10',   label: 'CGNAT / Shared',       desc: 'RFC 6598 — 4 M hosts',  type: 'internal' },
  { cidr: '169.254.0.0/16',  label: 'Link-Local',           desc: 'APIPA / sin servidor DHCP', type: 'internal' },
  { cidr: '198.18.0.0/15',   label: 'Benchmark testing',    desc: 'RFC 2544',               type: 'internal' },
];

const IPV6_BLOCKS = [
  { cidr: 'fd00::/8',        label: 'ULA — Privado (fd)',   desc: 'RFC 4193 — uso interno', type: 'internal' },
  { cidr: 'fc00::/7',        label: 'ULA — Completo (fc/fd)',desc: 'RFC 4193 — rango total', type: 'internal' },
  { cidr: 'fe80::/10',       label: 'Link-Local',           desc: 'Asignación automática',  type: 'internal' },
  { cidr: '2001:db8::/32',   label: 'Documentación / Test', desc: 'RFC 3849 — solo pruebas',type: 'internal' },
  { cidr: '2001::/32',       label: 'Teredo tunneling',     desc: 'RFC 4380',               type: 'internal' },
  { cidr: '64:ff9b::/96',    label: 'NAT64',                desc: 'RFC 6052',               type: 'internal' },
];

/* Detect if a CIDR is private/internal */
function isPrivateCidr(cidr) {
  if (!cidr) return true;
  const c = cidr.toLowerCase().trim();
  if (c.startsWith('10.') || c.startsWith('172.1') || c.startsWith('172.2') ||
      c.startsWith('172.3') || c.startsWith('192.168.') ||
      c.startsWith('100.64') || c.startsWith('fd') || c.startsWith('fc') ||
      c.startsWith('fe80') || c.startsWith('169.254')) return true;
  return false;
}

// Format block address count — IPv6 blocks have astronomically large counts
function formatBlockAddresses(b) {
  if (b.version === 6) {
    const prefix = b.cidr ? parseInt(b.cidr.split('/')[1], 10) : null;
    return prefix != null ? `2^${128 - prefix} dirs` : 'IPv6';
  }
  return `${(b.block_addresses || 0).toLocaleString()} IPs`;
}

const OCCUPATION_RANGES = {
  low:  { label: '< 50 %',    test: (pct) => pct > 0 && pct < 50 },
  mid:  { label: '50 – 80 %', test: (pct) => pct >= 50 && pct <= 80 },
  high: { label: '> 80 %',    test: (pct) => pct > 80 },
  none: { label: 'Sin uso',   test: (pct) => !pct },
};

const STATUS_META = {
  planned: { label: 'Planificado', badge: 'list-badge--warn' },
  active:  { label: 'Activo',      badge: 'list-badge--on' },
  retired: { label: 'Retirado',    badge: 'list-badge--off' },
};

export default function IPAMBlocks({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const errTimer = useRef(null);
  const okTimer  = useRef(null);

  const [blocks, setBlocks] = useState([]);

  const [search, setSearch]             = useState('');
  const [filterVersion, setFilterVersion] = useState('');
  const [filterSite, setFilterSite]     = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOwner, setFilterOwner]   = useState('');
  const [filterOccupation, setFilterOccupation] = useState('');
  const [filterSubnets, setFilterSubnets] = useState('');
  const [grouped, setGrouped]           = useState(true);
  const [hlId, setHlId]                 = useState(null);

  const [knownSites, setKnownSites] = useState([]);
  const [editTarget, setEditTarget] = useState(null); // bloque en edición
  const [editSaving, setEditSaving] = useState(false);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitial, setWizardInitial] = useState(null);  // copia de bloque

  /* Borrado en cascada */
  const [deleteTarget, setDeleteTarget] = useState(null); // { block, preview }
  const [deleteError, setDeleteError]   = useState('');
  const [deleting, setDeleting]         = useState(false);

  useEffect(() => {
    if (error) {
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setError(''), 6000);
    }
    return () => clearTimeout(errTimer.current);
  }, [error]);

  useEffect(() => {
    if (success) {
      clearTimeout(okTimer.current);
      okTimer.current = setTimeout(() => setSuccess(''), 9000);
    }
    return () => clearTimeout(okTimer.current);
  }, [success]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.getIpamBlocks(companyId);
      setBlocks(res.data || []);
      setKnownSites(prev => {
        const merged = new Set(prev);
        (res.data || []).forEach(row => { if (row.site) merged.add(row.site); });
        return [...merged].sort((a, b) => compareText(a, b));
      });
    } catch (e) {
      setError(e.message || 'Error cargando bloques');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Resalta el bloque indicado con ?destacar=<id>
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !blocks.length) return undefined;
    const id = Number(raw);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      return next;
    }, { replace: true });
    setHlId(id);
    const row = document.getElementById(`block-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, blocks, setSearchParams]);

  /* ── Filtrado ─────────────────────────────────────────────────────────── */
  const ownerOptions = useMemo(
    () => [...new Set(blocks.map((b) => (b.owner || '').trim()).filter(Boolean))].sort((a, b) => compareText(a, b)),
    [blocks],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const range = OCCUPATION_RANGES[filterOccupation];
    return blocks.filter((b) => {
      if (filterVersion && String(b.version) !== filterVersion) return false;
      if (filterSite && (b.site || '') !== filterSite) return false;
      if (filterType && (b.type || 'internal') !== filterType) return false;
      if (filterStatus && (b.status || 'active') !== filterStatus) return false;
      if (filterOwner && (b.owner || '') !== filterOwner) return false;
      if (range && !range.test(b.occupation_pct || 0)) return false;
      if (filterSubnets === 'with' && !(b.subnets_count > 0)) return false;
      if (filterSubnets === 'without' && b.subnets_count > 0) return false;
      if (!needle) return true;
      return (b.cidr || '').toLowerCase().includes(needle)
        || (b.name || '').toLowerCase().includes(needle)
        || (b.description || '').toLowerCase().includes(needle)
        || (b.site || '').toLowerCase().includes(needle)
        || (b.owner || '').toLowerCase().includes(needle);
    });
  }, [blocks, search, filterVersion, filterSite, filterType, filterStatus, filterOwner, filterOccupation, filterSubnets]);

  /** Etiqueta del grupo cuando se agrupa por sitio. Si el toggle está apagado
      no se pasa `groupBy` (devolver `null` creaba un grupo «Sin valor»). */
  const groupLabelOf = useCallback(
    (block) => block.site || tr('ipam.no_site', 'Sin site'),
    [tr],
  );

  const handleSaveBlock = useCallback(async (data) => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await api.updateIpamBlock(editTarget.id, data, companyId);
      setEditTarget(null);
      await load();
    } catch (e) {
      setError(e.message || 'Error actualizando el bloque');
    } finally {
      setEditSaving(false);
    }
  }, [editTarget, companyId, load]);

  /* ── Copiar bloque: precarga el asistente con los datos del original ───── */
  const openCopy = useCallback((block) => {
    setWizardInitial({
      cidr: block.cidr,
      version: block.version === 6 ? 6 : 4,
      type: block.type || 'internal',
      name: block.name ? `${block.name} (copia)` : '',
      description: block.description || '',
      owner: block.owner || '',
      site: block.site || '',
    });
    setWizardOpen(true);
  }, []);

  /* ── Borrado en cascada: primero se pide la vista previa al backend ────── */
  const openDeletePreview = useCallback(async (block) => {
    setDeleteError('');
    try {
      const res = await api.getIpamBlockDeletePreview(block.id, companyId);
      setDeleteTarget({ block, preview: res.data });
    } catch (e) {
      setError(e.message || 'Error consultando el contenido del bloque');
    }
  }, [companyId]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await api.deleteIpamBlock(deleteTarget.block.id, companyId);
      const counts = res?.data?.counts || {};
      setSuccess(
        `Bloque ${deleteTarget.block.cidr} eliminado: ${counts.subnets || 0} subred(es), ` +
        `${counts.ips || 0} IP(s)` +
        (counts.elements ? ` · ${counts.elements} elemento(s) desasociado(s).` : '.')
      );
      setDeleteTarget(null);
      load();
    } catch (e) {
      setDeleteError(e.message || 'Error eliminando el bloque');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, companyId, load]);

  /* ── Columnas (catálogo completo; 8 visibles de entrada) ──────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'cidr',
        label: tr('ipam.block_cidr', 'Bloque'),
        sortable: true,
        sortValue: (b) => b.cidr || '',
        render: (b) => <span className="list-code" style={{ fontWeight: 700 }}>{b.cidr}</span>,
      },
      { key: 'name', label: tr('ipam.name', 'Nombre'), sortable: true, render: (b) => b.name || dash },
      {
        key: 'version',
        label: tr('ipam.version', 'Versión'),
        sortable: true,
        sortValue: (b) => b.version || 4,
        render: (b) => <span className="list-badge list-code">v{b.version || 4}</span>,
      },
      {
        key: 'type',
        label: tr('ipam.type', 'Tipo'),
        sortable: true,
        sortValue: (b) => b.type || 'internal',
        render: (b) => (
          <span className="list-badge">
            {(b.type || 'internal') === 'external' ? tr('ipam.type_public', 'Público') : tr('ipam.type_private', 'Privado')}
          </span>
        ),
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (b) => b.status || 'active',
        render: (b) => {
          const meta = STATUS_META[b.status || 'active'] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      { key: 'owner', label: tr('ipam.owner', 'Propietario'), sortable: true, render: (b) => b.owner || dash },
      {
        key: 'site',
        label: tr('ipam.site', 'Site'),
        sortable: true,
        sortValue: (b) => b.site || '',
        render: (b) => b.site || dash,
      },
      {
        key: 'subnets_count',
        label: tr('ipam.subnets', 'Subredes'),
        sortable: true,
        sortValue: (b) => b.subnets_count || 0,
        render: (b) => (b.subnets_count
          ? <button
              type="button"
              className="list-badge list-badge--on"
              style={{ cursor: 'pointer' }}
              onClick={(event) => { event.stopPropagation(); navigate(`/networks/subnets?blockId=${b.id}`); }}
              title={tr('ipam.view_subnets', 'Ver las subredes del bloque')}
            >
              {b.subnets_count}
            </button>
          : <span className="list-hint">0</span>),
      },
      {
        key: 'occupation_pct',
        label: tr('ipam.occupation', 'Ocupación'),
        sortable: true,
        sortValue: (b) => b.occupation_pct || 0,
        render: (b) => {
          if (b.version === 6) {
            return <span className="list-hint">{formatBlockAddresses(b)}</span>;
          }
          const pct = Math.min(100, b.occupation_pct || 0);
          const tone = pct >= 90 ? 'var(--red-600)' : pct >= 70 ? 'var(--amber-600)' : 'var(--green-600)';
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block', width: 70, height: 7, borderRadius: 4,
                  background: 'var(--bg-toolbar)', overflow: 'hidden',
                }}
              >
                <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: tone }} />
              </span>
              <span className="list-hint">{b.occupation_pct || 0}%</span>
            </span>
          );
        },
      },
      {
        key: 'block_addresses',
        label: tr('ipam.addresses', 'Direcciones'),
        sortable: true,
        sortValue: (b) => b.block_addresses || 0,
        render: (b) => <span className="list-hint">{formatBlockAddresses(b)}</span>,
      },
      { key: 'description', label: tr('description', 'Descripción'), render: (b) => b.description || dash },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate]);

  const defaultVisible = ['cidr', 'name', 'version', 'type', 'status', 'site', 'subnets_count', 'occupation_pct'];

  const filters = [
    {
      key: 'version', label: tr('ipam.version', 'Versión'), allLabel: tr('ipam.version_all', 'IPv4 + IPv6'),
      value: filterVersion, onChange: setFilterVersion,
      options: [{ value: '4', label: 'IPv4' }, { value: '6', label: 'IPv6' }],
    },
    {
      key: 'site', label: tr('ipam.site', 'Site'), allLabel: tr('ipam.site_all', 'Todos los sites'),
      value: filterSite, onChange: setFilterSite,
      options: knownSites.map((s) => ({ value: s, label: s })),
    },
    {
      key: 'type', label: tr('ipam.type', 'Tipo'), allLabel: tr('ipam.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType,
      options: [
        { value: 'internal', label: tr('ipam.type_private', 'Privado') },
        { value: 'external', label: tr('ipam.type_public', 'Público') },
      ],
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'owner', label: tr('ipam.owner', 'Propietario'), allLabel: tr('ipam.owner_all', 'Todos los propietarios'),
      value: filterOwner, onChange: setFilterOwner,
      options: ownerOptions.map((o) => ({ value: o, label: o })),
    },
    {
      key: 'occupation', label: tr('ipam.occupation', 'Ocupación'), allLabel: tr('ipam.occupation_all', 'Cualquier ocupación'),
      value: filterOccupation, onChange: setFilterOccupation,
      options: Object.entries(OCCUPATION_RANGES).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'subnets', label: tr('ipam.subnets', 'Subredes'), allLabel: tr('ipam.subnets_all', 'Con y sin subredes'),
      value: filterSubnets, onChange: setFilterSubnets,
      options: [
        { value: 'with', label: tr('ipam.subnets_with', 'Con subredes') },
        { value: 'without', label: tr('ipam.subnets_without', 'Sin subredes') },
      ],
    },
    {
      key: 'group', label: tr('ipam.group_site', 'Agrupar por site'),
      type: 'toggle', value: grouped, onValue: true,
      onChange: (value) => setGrouped(Boolean(value)),
    },
  ];

  const subtitle = `${blocks.length} ${tr('ipam.block_plural', 'bloques')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map((b) => ({
    'Bloque': b.cidr || '',
    'Nombre': b.name || '',
    'Versión': `v${b.version || 4}`,
    'Tipo': (b.type || 'internal') === 'external' ? 'Público' : 'Privado',
    'Estado': (STATUS_META[b.status || 'active'] || STATUS_META.active).label,
    'Propietario': b.owner || '',
    'Site': b.site || '',
    'Subredes': b.subnets_count || 0,
    'Ocupación (%)': b.version === 6 ? '' : (b.occupation_pct || 0),
    'Direcciones': formatBlockAddresses(b),
    'Descripción': b.description || '',
  })), [filtered]);

  /** Tarjetas: lo esencial del bloque, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((b) => {
          const meta = STATUS_META[b.status || 'active'] || STATUS_META.active;
          return (
            <div
              key={b.id}
              id={`block-card-${b.id}`}
              className={`list-card${b.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setEditTarget(b)}
              onKeyDown={(event) => { if (event.key === 'Enter') setEditTarget(b); }}
              title={`${tr('action.edit', 'Editar')} ${b.cidr}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title list-code">{b.cidr}</div>
                  {b.name && <span>{b.name}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">v{b.version || 4}</span>
                <span className="list-badge">
                  {(b.type || 'internal') === 'external' ? tr('ipam.type_public', 'Público') : tr('ipam.type_private', 'Privado')}
                </span>
                {b.site && <span>📍 {b.site}</span>}
                {b.owner && <span>👤 {b.owner}</span>}
                <span>{b.subnets_count || 0} {tr('ipam.subnets', 'subredes').toLowerCase()}</span>
                <span className="list-hint">{formatBlockAddresses(b)}</span>
                {b.version !== 6 && <span className="list-hint">{b.occupation_pct || 0}%</span>}
              </div>

              {b.description && <div className="list-card-meta"><span>{b.description}</span></div>}

              <div className="list-card-actions">{rowActions(b)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Vistas ───────────────────────────────────────────────────────────── */
  const BLOCK_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada bloque'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('ipam_blocks', 'list');

  if (!companyId) {
    return (
      <IPAMLayout>
        <div className="alert alert-warning">{tr('ipam.select_company', 'Selecciona una empresa para ver el IPAM.')}</div>
      </IPAMLayout>
    );
  }


  return (
    <IPAMLayout>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 d-flex justify-content-between">
          <span>{success}</span>
          <button className="btn-close" onClick={() => setSuccess('')} />
        </div>
      )}

      <ListPage
        icon={GlobeAltIcon}
        title={tr('ipam.blocks_title', 'Bloques IP')}
        subtitle={subtitle}

        onCreate={() => { setWizardInitial(null); setWizardOpen(true); }}
        createLabel={tr('ipam.block_new', 'Nuevo bloque')}
        createTitle={tr('ipam.block_new_hint', 'Crear un bloque de direcciones (IPv4 o IPv6)')}

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={tr('ipam.block_search', 'Buscar CIDR, nombre, site, propietario…')}
        filters={filters}

        exportRows={exportRows}
        exportFileBase="ipam-bloques"
        exportSheetName="Bloques IP"

        columns={columns}
        columnStorageKey="cmdb_cols_ipam_blocks"
        defaultVisible={defaultVisible}
        rows={filtered}
        rowKey={(b) => b.id}
        rowId={(b) => `block-row-${b.id}`}
        rowClassName={(b) => (b.id === hlId ? 'list-row-highlight' : '')}
        onRowClick={setEditTarget}
        rowTitle={(b) => `${tr('action.edit', 'Editar')} ${b.cidr}`}
        groupBy={grouped ? groupLabelOf : undefined}

        views={BLOCK_VIEWS}
        listKey="ipam_blocks"
        defaultView="list"

        loading={loading}
        loadingText={tr('msg.loading', 'Cargando bloques…')}

        empty={{
          icon: '🌐',
          title: blocks.length === 0
            ? tr('ipam.block_empty', 'No hay bloques IP')
            : tr('msg.no_results', 'Ningún bloque coincide con los filtros'),
          subtitle: blocks.length === 0
            ? tr('ipam.block_empty_hint', 'Crea el primero para empezar a repartir direcciones.')
            : undefined,
          action: blocks.length === 0 ? (
            <button
              type="button"
              className="list-btn-primary"
              onClick={() => { setWizardInitial(null); setWizardOpen(true); }}
            >
              ＋ {tr('ipam.block_new', 'Nuevo bloque')}
            </button>
          ) : undefined,
        }}

        actions={{
          extra: (b) => (
            <>
              <button
                type="button"
                className="list-action-btn"
                onClick={() => navigate(`/networks/subnets?blockId=${b.id}&new=1`)}
                title={tr('ipam.block_add_subnet', 'Añadir una subred a este bloque')}
              >
                <PlusIcon />
              </button>
              <button
                type="button"
                className="list-action-btn"
                onClick={() => navigate(`/networks/subnets?blockId=${b.id}`)}
                disabled={!b.subnets_count}
                title={tr('ipam.block_view_subnets', 'Ver las subredes del bloque')}
              >
                <QueueListIcon />
              </button>
              <button
                type="button"
                className="list-action-btn"
                onClick={() => navigate(`/networks/explorer?blockId=${b.id}`)}
                disabled={!b.subnets_count}
                title={tr('ipam.block_view_explorer', 'Ver el bloque en el Explorer')}
              >
                <MagnifyingGlassIcon />
              </button>
            </>
          ),
          onCopy: openCopy,
          onEdit: setEditTarget,
          onDelete: openDeletePreview,
          deleteTitle: tr('ipam.block_delete_title', 'Eliminar bloque'),
          deleteMessage: (b) => (
            <>
              {tr('ipam.block_delete_confirm', 'Vas a eliminar el bloque')} <strong>{b.cidr}</strong>
              {b.name ? ` (${b.name})` : ''}.{' '}
              {tr('ipam.block_delete_hint', 'Antes de borrar se mostrará todo lo que arrastra (subredes, IPs, elementos que se quedarán sin red). No se puede deshacer.')}
            </>
          ),
        }}
      />

      {/* Asistente de creación (también precargado al copiar un bloque) */}
      {wizardOpen && (
        <BlockWizard
          companyId={companyId}
          existingBlocks={blocks}
          knownSites={knownSites}
          initial={wizardInitial}
          onCreated={() => { load(); setWizardOpen(false); }}
          onClose={() => { setWizardOpen(false); setWizardInitial(null); }}
        />
      )}

      {editTarget && (
        <BlockEditModal
          block={editTarget}
          knownSites={knownSites}
          saving={editSaving}
          onSave={handleSaveBlock}
          onClose={() => setEditTarget(null)}
        />
      )}

      {deleteTarget && (
        <IpamCascadeDeleteModal
          title={`Eliminar bloque ${deleteTarget.block.cidr}`}
          subtitle={deleteTarget.block.name || ''}
          preview={deleteTarget.preview}
          busy={deleting}
          error={deleteError}
          onClose={() => { if (!deleting) { setDeleteTarget(null); setDeleteError(''); } }}
          onConfirm={confirmDelete}
        />
      )}
    </IPAMLayout>
  );
}

/* ── Edición de un bloque (sitio, nombre, descripción, estado) ─────────────── */
function BlockEditModal({ block, knownSites = [], saving, onSave, onClose }) {
  const [form, setForm] = useState({
    name: block.name || '',
    description: block.description || '',
    owner: block.owner || '',
    site: block.site || '',
    status: block.status || 'active',
  });

  const submit = (event) => {
    event.preventDefault();
    onSave({
      name: form.name.trim() || null,
      description: form.description.trim() || null,
      owner: form.owner.trim() || null,
      site: form.site.trim() || null,
      status: form.status,
    });
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }}>
        <div className="modal-content shadow-lg">
          <div className="modal-header py-3 border-bottom">
            <div>
              <h6 className="modal-title fw-bold mb-0">Editar bloque</h6>
              <small className="text-muted" style={{ fontFamily: 'monospace' }}>{block.cidr}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body px-4 py-3 d-grid gap-2">
              <div>
                <label className="form-label small mb-1">Sitio / Localización</label>
                <input
                  className="form-control form-control-sm"
                  value={form.site}
                  list="block-edit-sites"
                  placeholder="Ej. Madrid-Central"
                  onChange={e => setForm(f => ({ ...f, site: e.target.value }))}
                />
                <datalist id="block-edit-sites">
                  {knownSites.map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div>
                <label className="form-label small mb-1">Nombre</label>
                <input
                  className="form-control form-control-sm"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="form-label small mb-1">Descripción</label>
                <input
                  className="form-control form-control-sm"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label small mb-1">Responsable</label>
                  <input
                    className="form-control form-control-sm"
                    value={form.owner}
                    onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Estado</label>
                  <select
                    className="form-select form-select-sm"
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  >
                    <option value="active">Activo</option>
                    <option value="planned">Planificado</option>
                    <option value="retired">Retirado</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer py-2">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ── Block creation wizard ─────────────────────────────────────────────────── */
function BlockWizard({ companyId, existingBlocks, knownSites = [], initial = null, onCreated, onClose }) {
  const STEPS = ['Versión', 'Bloque', 'Detalles'];
  /* `initial` se rellena al COPIAR un bloque: se precargan versión, tipo,
     nombre, propietario y site, y se salta directo al paso del CIDR, que es
     lo único que no se puede reutilizar (los CIDR son únicos por empresa). */
  const [step, setStep]           = useState(initial ? 1 : 0);
  const [version, setVersion]     = useState(initial?.version || 4);
  const [selCidr, setSelCidr]     = useState('');
  const [customCidr, setCustomCidr] = useState('');
  const [blockType, setBlockType] = useState(initial?.type || 'internal');
  const [name, setName]           = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [owner, setOwner]         = useState(initial?.owner || '');
  const [site, setSite]           = useState(initial?.site || '');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const catalogue     = version === 4 ? IPV4_BLOCKS : IPV6_BLOCKS;
  const existingCidrs = new Set(existingBlocks.map(b => b.cidr));
  const finalCidr     = selCidr || customCidr.trim();

  /* Auto-detect public/private when CIDR changes */
  useEffect(() => {
    if (finalCidr) setBlockType(isPrivateCidr(finalCidr) ? 'internal' : 'external');
  }, [finalCidr]);

  const canNext = () => {
    if (step === 0) return true;
    if (step === 1) return finalCidr.length > 0;
    return true;
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.createIpamBlock(
        { cidr: finalCidr, type: blockType, name, description, owner, site: site.trim() || null },
        companyId,
      );
      onCreated();
    } catch (err) {
      setError(err.message || 'Error al crear bloque');
      setSaving(false);
    }
  };

  return (
    <div
      className="modal d-block"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content shadow-lg">

          {/* Header */}
          <div className="modal-header pb-2">
            <h5 className="modal-title">
              {initial
                ? `Copiar bloque IP · origen ${initial.cidr}`
                : 'Nuevo bloque IP'}
            </h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          {/* Step indicator */}
          <div className="px-4 pt-3 d-flex gap-2 align-items-center">
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                <div className="d-flex flex-column align-items-center" style={{ minWidth: 72 }}>
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center fw-bold`}
                    style={{
                      width: 32, height: 32, fontSize: '0.9em',
                      background: i < step ? '#198754' : i === step ? '#0d6efd' : '#dee2e6',
                      color: i <= step ? '#fff' : '#6c757d',
                    }}
                  >
                    {i < step ? '✓' : i + 1}
                  </div>
                  <small style={{ fontSize: '0.7em', color: i === step ? '#0d6efd' : '#6c757d' }}>{s}</small>
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: i < step ? '#198754' : '#dee2e6', marginBottom: 18 }} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="modal-body pt-2">
            {error && <div className="alert alert-danger py-2">{error}</div>}

            {/* ── Step 0: choose IP version ── */}
            {step === 0 && (
              <div>
                <p className="text-muted mb-3">¿Qué tipo de bloque vas a crear?</p>
                <div className="row g-3">
                  {[4, 6].map(v => (
                    <div key={v} className="col-6">
                      <div
                        className={`card h-100 text-center p-3 ${version === v ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                        role="button"
                        style={{ cursor: 'pointer' }}
                        onClick={() => { setVersion(v); setSelCidr(''); setCustomCidr(''); }}
                      >
                        <div style={{ fontSize: '2.2em' }}>{v === 4 ? '🌐' : '🔷'}</div>
                        <strong>IPv{v}</strong>
                        <div className="text-muted" style={{ fontSize: '0.82em' }}>
                          {v === 4 ? 'Rangos RFC 1918 y públicos' : 'Rangos ULA (fd/fc) y globales'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Step 1: pick from catalogue or enter custom ── */}
            {step === 1 && (
              <div>
                <p className="text-muted mb-2">Selecciona un rango estándar IPv{version} o introduce uno personalizado.</p>
                <div className="row g-2 mb-3">
                  {catalogue.map(item => {
                    const already = existingCidrs.has(item.cidr);
                    const active  = selCidr === item.cidr;
                    return (
                      <div key={item.cidr} className="col-md-6">
                        <div
                          className={`card p-2 ${active ? 'border-primary bg-primary bg-opacity-10' : already ? 'border-secondary opacity-50' : 'border'}`}
                          role={already ? undefined : 'button'}
                          style={{ cursor: already ? 'not-allowed' : 'pointer', fontSize: '0.84em' }}
                          onClick={() => { if (!already) { setSelCidr(item.cidr); setCustomCidr(''); }}}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{item.cidr}</span>
                            {already && <span className="badge text-bg-secondary" style={{ fontSize: '0.7em' }}>Ya existe</span>}
                          </div>
                          <div className="fw-semibold">{item.label}</div>
                          <div className="text-muted">{item.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '0.85em', whiteSpace: 'nowrap' }}>O introduce manualmente:</span>
                  <input
                    className="form-control form-control-sm"
                    placeholder={version === 4 ? 'p.ej. 203.0.113.0/24' : 'p.ej. 2001:db8::/32'}
                    value={customCidr}
                    onChange={e => { setCustomCidr(e.target.value); setSelCidr(''); }}
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
              </div>
            )}

            {/* ── Step 2: description, type, owner ── */}
            {step === 2 && (
              <form onSubmit={create} id="wizard-form">
                <div className="mb-2">
                  <label className="form-label form-label-sm">CIDR seleccionado</label>
                  <input className="form-control form-control-sm" readOnly value={finalCidr} style={{ fontFamily: 'monospace', background: '#f8f9fa' }} />
                </div>
                <div className="mb-2">
                  <label className="form-label form-label-sm">Nombre <span className="text-muted">(opcional)</span></label>
                  <input className="form-control form-control-sm" placeholder="Red corporativa principal…" value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div className="mb-2">
                  <label className="form-label form-label-sm">Tipo</label>
                  <select className="form-select form-select-sm" value={blockType} onChange={e => setBlockType(e.target.value)}>
                    <option value="internal">Privado / Interno</option>
                    <option value="external">Público / Externo</option>
                  </select>
                  <div className="form-text">
                    {isPrivateCidr(finalCidr) ? '✓ Detectado automáticamente como rango privado.' : '✓ Detectado automáticamente como rango público.'}
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label form-label-sm">Descripción <span className="text-muted">(opcional)</span></label>
                  <input className="form-control form-control-sm" placeholder="Rango corporativo principal…" value={description} onChange={e => setDescription(e.target.value)} />
                </div>
                <div className="mb-2">
                  <label className="form-label form-label-sm">Sitio / Localización <span className="text-muted">(opcional)</span></label>
                  <input className="form-control form-control-sm" placeholder="Ej. Madrid-Central" value={site} onChange={e => setSite(e.target.value)} list="ipam-block-sites" />
                  <datalist id="ipam-block-sites">
                    {knownSites.map(s => <option key={s} value={s} />)}
                  </datalist>
                  <div className="form-text">Sirve para agrupar el listado por sede.</div>
                </div>
                <div className="mb-2">
                  <label className="form-label form-label-sm">Responsable / Owner <span className="text-muted">(opcional)</span></label>
                  <input className="form-control form-control-sm" placeholder="netops@empresa.com" value={owner} onChange={e => setOwner(e.target.value)} />
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer pt-2">
            {step > 0 && (
              <button className="btn btn-outline-secondary btn-sm me-auto" onClick={() => setStep(s => s - 1)} disabled={saving}>
                ← Anterior
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button className="btn btn-primary btn-sm" onClick={() => setStep(s => s + 1)} disabled={!canNext()}>
                Siguiente →
              </button>
            ) : (
              <button className="btn btn-success btn-sm" type="submit" form="wizard-form" disabled={saving || !finalCidr}>
                {saving ? 'Creando…' : '✓ Crear bloque'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
